import { z } from 'zod';
import { ALERT_SEVERITIES } from '../constants/severities';
import { baseEntityFields, idSchema, instanceIdSchema, isoDateTimeSchema } from './common.schema';
import { datapointNameSchema, sensorKeySchema } from './sensor.schema';
import { SDT_SCOPE_TYPES, sdtRecurrenceSchema, sdtWindowShapeRules } from './alerting.schema';
/**
 * Change participation (#26; docs/architecture/change-participation.md).
 *
 * A change window is planned work Detect participates in. The window document
 * is IMMUTABLE (§8, option 3): everything that accretes — the pre-window
 * snapshot, witness-impact observations, the verdict — is a separate record
 * keyed `(changeWindowId, occurrenceStartsAt)`.
 */
/** A typed scope reference — the same taxonomy as SDT. Canonical form: a
 *  `tenant` scope carries no target; every other scope requires one. */
export const changeScopeRefSchema = z
    .object({
    scopeType: z.enum(SDT_SCOPE_TYPES),
    targetId: idSchema.nullable().optional(),
})
    .strict()
    .superRefine((val, ctx) => {
    if (val.scopeType === 'tenant') {
        if (val.targetId) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['targetId'], message: 'a tenant scope carries no targetId' });
        }
    }
    else if (!val.targetId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['targetId'],
            message: `targetId is required for scopeType "${val.scopeType}"`,
        });
    }
});
/** Stored form: `targetId` is always present (null for tenant). */
export const storedChangeScopeRefSchema = z
    .object({ scopeType: z.enum(SDT_SCOPE_TYPES), targetId: idSchema.nullable() })
    .strict();
export const MAX_CHANGE_SCOPE_REFS = 50;
/**
 * Change INTENT (§6): what the change is for, and where it came from. It
 * arrives from outside — Desk, an external ITSM, or Detect's own UI — so it is
 * deliberately NOT Desk-shaped. `externalRef` is the authoring system's own id
 * (opaque); with `source` it makes a re-delivery idempotent.
 */
export const CHANGE_INTENT_SOURCES = ['desk', 'external', 'detect'];
export const CHANGE_PURPOSES = ['transparent', 'remediation', 'migration', 'maintenance'];
/**
 * Rung 3 (#153): how "did the change achieve its intent" is decided, chosen when the window is created.
 *  - `none`: not graded (the default; the free-text `successCriteria` stays a description).
 *  - `structured`: a short list of criteria graded exactly from metrics, alerts and sensor state.
 *  - `human`: only a person's mark decides.
 *  - `ai`: Gemini on Vertex AI (Detect's own GCP project) judges from the change's stated intent and a compact
 *    before/after summary of its in-scope resources. Opt-in per tenant; a failed or unavailable call is `unclear`.
 * A person's mark is available in EVERY mode and overrides a machine result (both are kept).
 */
export const CHANGE_GRADING_MODES = ['none', 'structured', 'human', 'ai'];
export const MAX_CHANGE_CRITERIA = 10;
export const MAX_CRITERION_MINUTES = 24 * 60;
const criterionSeries = {
    resourceId: idSchema,
    sensorKey: sensorKeySchema,
    /** Null = the resource-level series of a sensor with no instances. */
    instanceId: instanceIdSchema.nullable().default(null),
};
const minutes = z.number().int().min(1).max(MAX_CRITERION_MINUTES);
/**
 * One structured success criterion. Each is graded exactly, as met / not met / indeterminate (no data), and
 * "no data" is never "met". T is measured from the occurrence's END.
 *  - `metric`: the latest datapoint of the series at or before end + T (and at or after end) is below / above X.
 *  - `alert-cleared`: the condition has no active alert at end + T and none started in [end, end + T), and the series is
 *    reporting then (a mistyped or silent series is indeterminate, never "cleared").
 *  - `sensor-ok`: at end + T the sensor is reporting (a datapoint within two collection intervals, at least five
 *    minutes) and has no active alert on that resource / instance.
 */
export const changeCriterionSchema = z.discriminatedUnion('kind', [
    z
        .object({
        kind: z.literal('metric'),
        ...criterionSeries,
        datapoint: datapointNameSchema,
        comparator: z.enum(['below', 'above']),
        threshold: z.number().finite(),
        withinMinutes: minutes,
    })
        .strict(),
    z
        .object({
        kind: z.literal('alert-cleared'),
        ...criterionSeries,
        datapoint: datapointNameSchema,
        forMinutes: minutes,
    })
        .strict(),
    z
        .object({
        kind: z.literal('sensor-ok'),
        ...criterionSeries,
        atMinutes: z.number().int().min(0).max(MAX_CRITERION_MINUTES),
    })
        .strict(),
]);
/** Minutes after the occurrence's end at which a criterion is graded. */
export function criterionDueMinutes(c) {
    return c.kind === 'metric' ? c.withinMinutes : c.kind === 'alert-cleared' ? c.forMinutes : c.atMinutes;
}
/** A structured window has criteria; any other mode has none: no contradictory intents. */
function gradingRules(val, ctx) {
    if (val.gradingMode === 'structured' && val.criteria.length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['criteria'], message: 'structured grading needs at least one criterion' });
    }
    if (val.gradingMode !== 'structured' && val.criteria.length > 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['criteria'], message: 'criteria are only used by structured grading' });
    }
}
const changeIntentObject = z
    .object({
    externalRef: z.string().trim().min(1).max(200),
    source: z.enum(CHANGE_INTENT_SOURCES),
    purpose: z.enum(CHANGE_PURPOSES),
    /** Free text; what "success" means for a remediation. The description; grading is `gradingMode`. */
    successCriteria: z.string().max(4000).optional(),
    notes: z.string().max(4000).optional(),
    gradingMode: z.enum(CHANGE_GRADING_MODES).default('none'),
    criteria: z.array(changeCriterionSchema).max(MAX_CHANGE_CRITERIA).default([]),
})
    .strict();
export const changeIntentSchema = changeIntentObject.superRefine(gradingRules);
/** Stored form: optional text is materialized as null. Older records lack the grading fields (none, no criteria). */
export const storedChangeIntentSchema = z
    .object({
    externalRef: z.string().min(1).max(200),
    source: z.enum(CHANGE_INTENT_SOURCES),
    purpose: z.enum(CHANGE_PURPOSES),
    successCriteria: z.string().max(4000).nullable(),
    notes: z.string().max(4000).nullable(),
    gradingMode: z.enum(CHANGE_GRADING_MODES).default('none'),
    criteria: z.array(changeCriterionSchema).max(MAX_CHANGE_CRITERIA).default([]),
})
    .strict()
    .superRefine(gradingRules);
/**
 * The inbound payload exactly as §6 specifies it. The window is nested; the
 * scope refs use the same taxonomy as SDT. It is mapped onto the same input the
 * Detect API takes, so there is ONE creation path and one set of rules.
 */
export const changeIntentPayloadSchema = z
    .object({
    externalRef: changeIntentObject.shape.externalRef,
    source: changeIntentObject.shape.source,
    title: z.string().trim().min(1).max(200),
    purpose: changeIntentObject.shape.purpose,
    window: z
        .object({
        startsAt: z.string().datetime({ offset: true }),
        endsAt: z.string().datetime({ offset: true }),
    })
        .strict(),
    expectedImpact: z.array(changeScopeRefSchema).min(1).max(MAX_CHANGE_SCOPE_REFS),
    witnesses: z.array(changeScopeRefSchema).max(MAX_CHANGE_SCOPE_REFS).default([]),
    successCriteria: changeIntentObject.shape.successCriteria,
    notes: changeIntentObject.shape.notes,
    gradingMode: changeIntentObject.shape.gradingMode,
    criteria: changeIntentObject.shape.criteria,
})
    .strict()
    .superRefine(gradingRules);
/** The window's timing, validated by the SAME rules as an SDT window. */
function timingRules(val, ctx) {
    sdtWindowShapeRules({ scopeType: 'tenant', targetId: null, startsAt: val.startsAt, endsAt: val.endsAt, recurrence: val.recurrence }, ctx);
}
export const changeWindowInputSchema = z
    .object({
    title: z.string().trim().min(1).max(200),
    comment: z.string().max(2000).optional(),
    /** One-time window (ISO datetimes), or recurrence — exactly one. */
    startsAt: z.string().datetime({ offset: true }).optional(),
    endsAt: z.string().datetime({ offset: true }).optional(),
    recurrence: sdtRecurrenceSchema.optional(),
    /** Suppressed for the window, verified at its end. At least one: a window
     *  with nothing expected to be disturbed is not a change window. */
    expectedImpact: z.array(changeScopeRefSchema).min(1).max(MAX_CHANGE_SCOPE_REFS),
    /** Must NOT be disturbed: never suppressed, watched for the duration. */
    witnesses: z.array(changeScopeRefSchema).max(MAX_CHANGE_SCOPE_REFS).default([]),
    /** What the change is for (§6). Optional: plain scheduled downtime is the
     *  degenerate case with none. */
    intent: changeIntentSchema.optional(),
})
    .strict()
    .superRefine(timingRules);
export const changeWindowSchema = z
    .object({
    title: z.string().min(1).max(200),
    comment: z.string().max(2000).nullable(),
    startsAt: z.string().datetime({ offset: true }).nullable(),
    endsAt: z.string().datetime({ offset: true }).nullable(),
    recurrence: sdtRecurrenceSchema.nullable(),
    expectedImpact: z.array(storedChangeScopeRefSchema).min(1).max(MAX_CHANGE_SCOPE_REFS),
    witnesses: z.array(storedChangeScopeRefSchema).max(MAX_CHANGE_SCOPE_REFS),
    /** The companion SDT windows (one per expected-impact ref) that do the
     *  suppression — the same mechanism, evaluator path and promotion
     *  semantics as every SDT. Written once at creation. */
    sdtWindowIds: z.array(idSchema).min(1).max(MAX_CHANGE_SCOPE_REFS),
    intent: storedChangeIntentSchema.nullable().default(null),
})
    .extend(baseEntityFields)
    .strict()
    .superRefine(timingRules);
/* ---------------- append-only records, per occurrence ---------------- */
export const CHANGE_VERDICTS = [
    'clean',
    'did-not-recover',
    'got-worse',
    'blast-radius',
    'indeterminate',
    'abandoned',
];
const occurrenceFields = {
    changeWindowId: idSchema,
    /** The occurrence's scheduled start: the one-time `startsAt`, or the instant a
     *  recurring window opened. With the window id it IS the occurrence. */
    occurrenceStartsAt: isoDateTimeSchema,
};
/** The conditions a snapshot holds are keyed by the alert's existing dedupKey. */
export const snapshotConditionSchema = z
    .object({
    dedupKey: z.string().min(1).max(700),
    severity: z.enum(ALERT_SEVERITIES),
    role: z.enum(['expected', 'witness']),
})
    .strict();
export const MAX_SNAPSHOT_CONDITIONS = 5000;
export const MAX_DEMOTIONS = 500;
/** The pre-window state, one per occurrence. Content is immutable; `closedAt`
 *  is a write-once null → instant bookkeeping field set in the same transaction
 *  as the verdict, so "which occurrences still await verification" is a query. */
export const changeSnapshotSchema = z
    .object({
    ...occurrenceFields,
    occurrenceEndsAt: isoDateTimeSchema,
    capturedAt: isoDateTimeSchema,
    conditions: z.array(snapshotConditionSchema).max(MAX_SNAPSHOT_CONDITIONS),
    /** More conditions existed than fit: the comparison would be unsound. */
    truncated: z.boolean(),
    /** Captured too long after the occurrence's start (a delayed sweep) to be the
     *  PRE-window state: a condition raised in between would read as pre-existing.
     *  The comparison would be unsound, so the verdict is `indeterminate`. */
    late: z.boolean(),
    /** Witness targets that, at this occurrence's start, had come to overlap
     *  expected impact (a resource added to a client since creation). They are
     *  graded as expected impact, and the verdict says so (§2.2). */
    demotedWitnessKeys: z.array(z.string().max(900)).max(MAX_DEMOTIONS),
    /** Write-once bookkeeping: null until the verdict is recorded. */
    closedAt: isoDateTimeSchema.nullable(),
    /** Write-once bookkeeping for rung 2 (#153): null until the baseline comparison has been recorded (or given
     *  up on), set in the same transaction as the assessment. A snapshot from before rung 2 lacks the field and is
     *  never swept for it. */
    baselineAssessedAt: isoDateTimeSchema.nullable().default(null),
    /** The same write-once marker for rung 3's structured grading (#153): present (null) only for a window whose
     *  `gradingMode` is `structured`; set in the same transaction as the intent assessment. Absent = nothing for the
     *  sweep to grade (any other mode, or a snapshot from before rung 3). */
    intentAssessedAt: isoDateTimeSchema.nullable().optional(),
    /** When the AI option last made (or began) a model call for this occurrence: spaces retries, without which a
     *  retry would have to be guessed from the clock. Bookkeeping only; set while the marker is open. */
    intentLastAttemptAt: isoDateTimeSchema.nullable().optional(),
})
    .extend(baseEntityFields)
    .strict();
export const verdictFindingSchema = z
    .object({
    role: z.enum(['expected', 'witness']),
    dedupKey: z.string().min(1).max(700),
    /** `new`: not in the snapshot. `worse`: in the snapshot, now more severe. */
    kind: z.enum(['new', 'worse']),
    severity: z.enum(ALERT_SEVERITIES),
    snapshotSeverity: z.enum(ALERT_SEVERITIES).nullable(),
})
    .strict();
export const INDETERMINATE_REASONS = ['no-fresh-data', 'collector-down', 'snapshot-truncated', 'snapshot-late'];
export const MAX_VERDICT_FINDINGS = 200;
/**
 * Rung 2 (change-participation.md §3.1, #153): "do the in-scope resources look normal against their learned
 * baseline (#18) after the change?". Advisory: it is recorded beside the verdict and never changes it.
 */
export const CHANGE_BASELINE_MODEL = {
    /** How long after the window's END the post-window value is observed: the comparison is always over
     *  `[end, end + settleMinutes)`. The verdict itself is recorded at its normal time and is never held back for it
     *  (rung 2 is a separate record written once this period has passed), so a slow or failed comparison cannot delay
     *  or change a verdict. */
    settleMinutes: 30,
    /** Series reported individually (the worst first); the counts always cover all of them. */
    maxFindings: 100,
    /** Per-query BigQuery cost cap (bytes billed). */
    maxBytesBilled: 512 * 1024 ** 2,
};
export const BASELINE_FINDINGS_STATUSES = ['assessed', 'no-baseline', 'no-data'];
/** One series observed outside its normal band after the window. */
export const baselineFindingSchema = z
    .object({
    resourceId: idSchema,
    sensorKey: z.string().min(1).max(200),
    instanceId: z.string().max(200).nullable(),
    datapoint: z.string().min(1).max(200),
    /** Mean of the series over the observed span. */
    observed: z.number().finite(),
    /** The baseline the value was compared with: the same hour(-of-week) bucket, or the series-wide one when
     *  that bucket has too few samples to trust. */
    basis: z.enum(['bucket', 'overall']),
    p05: z.number().finite().nullable(),
    p50: z.number().finite().nullable(),
    p95: z.number().finite().nullable(),
    position: z.enum(['below', 'above']),
    /** Distance from the median in robust sigmas; null when the series has no spread. */
    robustZ: z.number().finite().nullable(),
    /** Datapoints behind `observed`. */
    samples: z.number().int().positive(),
})
    .strict();
const baselineFindingsObject = z
    .object({
    /** `assessed`: at least one series had both post-window data and a baseline.
     *  `no-baseline`: nothing was compared and at least one series with data has no baseline yet (too little
     *  history). `noData` may ALSO be non-zero here (baselined series that did not report): both counts are on the
     *  record and both are shown. NOT "normal".
     *  `no-data`: nothing was compared and no series with data lacks a baseline: nothing reported in the observed
     *  span (this includes an emptied scope, and nothing reporting and nothing having a baseline either). NOT "normal". */
    status: z.enum(BASELINE_FINDINGS_STATUSES),
    /** The span the post-window value is the mean of: always `[end, end + settleMinutes)` of the occurrence. */
    observedFrom: isoDateTimeSchema,
    observedTo: isoDateTimeSchema,
    settleMinutes: z.number().int().nonnegative(),
    /** Series with both post-window data and a baseline. */
    assessed: z.number().int().nonnegative(),
    withinNormal: z.number().int().nonnegative(),
    outside: z.number().int().nonnegative(),
    /** Series with post-window data but no baseline yet. */
    noBaseline: z.number().int().nonnegative(),
    /** Baselined series with no data in the observed span. */
    noData: z.number().int().nonnegative(),
    findings: z.array(baselineFindingSchema).max(CHANGE_BASELINE_MODEL.maxFindings),
    findingsTruncated: z.boolean(),
})
    .strict();
/** The counts must add up and the list must be the outside series: a record that contradicts itself is refused. */
function baselineFindingsRules(val, ctx) {
    if (val.assessed !== val.withinNormal + val.outside) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['assessed'], message: 'assessed must equal withinNormal + outside' });
    }
    if ((val.status === 'assessed') !== (val.assessed > 0)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['status'], message: 'status "assessed" exactly when assessed > 0' });
    }
    if (val.findings.length > val.outside || (!val.findingsTruncated && val.findings.length !== val.outside)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['findings'], message: 'findings must list every outside series unless truncated' });
    }
}
export const baselineFindingsSchema = baselineFindingsObject.superRefine(baselineFindingsRules);
/** The verdict, exactly one per `(changeWindowId, occurrenceStartsAt)` — its
 *  document id is derived from that pair and created only if absent. */
export const changeVerdictSchema = z
    .object({
    ...occurrenceFields,
    occurrenceEndsAt: isoDateTimeSchema,
    verdict: z.enum(CHANGE_VERDICTS),
    /** The instant the verdict is ABOUT: `endsAt`, or the deletion instant for
     *  `abandoned`. Waiting to learn the answer never moves it (§4.2). */
    assertedAt: isoDateTimeSchema,
    /** When Detect actually computed it. */
    verifiedAt: isoDateTimeSchema,
    /** Highest health-ladder rung used: 0 = "it didn't get worse" (the floor, and the only rung that
     *  decides the verdict); 1 = "returned to original state" was also assessed (`returnedToOriginal`);
     *  Rung 2 (normal against the learned baseline) is NOT stored here: the verdict is immutable and the baseline
     *  comparison needs the settle period after the window, so it is a separate record
     *  (`changeBaselineAssessmentSchema`) that the API presents beside the verdict as rung 2.
     *  Rung 3 (intent grading) is not built yet. Rungs above 0 are advisory. */
    rung: z.union([z.literal(0), z.literal(1)]),
    /** Rung 1 (§3.1): how the in-scope conditions compare with the pre-window snapshot. Informational —
     *  it never changes the verdict (a better-than-before system is not a failed change). Null when the
     *  verdict was not graded (`indeterminate`). */
    returnedToOriginal: z
        .object({
        /** Every snapshot condition is still there at the same severity, and nothing was added. */
        identical: z.boolean(),
        /** Snapshot conditions still active at the same severity. */
        unchanged: z.number().int().nonnegative(),
        /** Snapshot conditions now cleared or at a lower severity (never a failure). */
        improved: z.number().int().nonnegative(),
    })
        .strict()
        .nullable()
        .default(null),
    findings: z.array(verdictFindingSchema).max(MAX_VERDICT_FINDINGS),
    findingsTruncated: z.boolean(),
    /** Witness-impact observations recorded during the window. */
    witnessImpactCount: z.number().int().nonnegative(),
    demotedWitnessKeys: z.array(z.string().max(900)).max(MAX_DEMOTIONS),
    indeterminate: z
        .object({
        reason: z.enum(INDETERMINATE_REASONS),
        /** Resources with no observation at/after the deadline, or the down collectors. */
        subjects: z.array(idSchema).max(50),
    })
        .strict()
        .nullable(),
    /** Who ended it early, for `abandoned`. */
    abandonedBy: idSchema.nullable(),
})
    .extend(baseEntityFields)
    .strict()
    .superRefine((val, ctx) => {
    // Rung 1 was assessed exactly when its result exists: no contradictory records.
    if ((val.rung === 1) !== (val.returnedToOriginal !== null)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['returnedToOriginal'],
            message: 'returnedToOriginal is present exactly when rung is 1',
        });
    }
    // An indeterminate verdict compared nothing, so it is never graded.
    if (val.verdict === 'indeterminate' && val.rung !== 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['rung'], message: 'an indeterminate verdict is ungraded (rung 0)' });
    }
});
/** How long after the settle period a failing comparison is retried before it is given up on (a verdict with no
 *  baseline comparison is then reported as "not assessed", never as normal). */
export const BASELINE_ASSESSMENT_GIVE_UP_HOURS = 6;
/** Rung 2's result, one per `(changeWindowId, occurrenceStartsAt)`, written once at the end of the settle period
 *  (its document id is derived from the pair and created only if absent). Advisory: it never changes the verdict. */
export const changeBaselineAssessmentSchema = baselineFindingsObject
    .extend({
    ...occurrenceFields,
    assessedAt: isoDateTimeSchema,
})
    .extend(baseEntityFields)
    .strict()
    .superRefine(baselineFindingsRules);
/* ---------------- rung 3 (#153): did it achieve its intent ---------------- */
export const CRITERION_RESULTS = ['met', 'not_met', 'indeterminate'];
export const INTENT_RESULTS = ['achieved', 'not_achieved', 'unclear'];
/** All criteria met = achieved; any not met = not achieved; otherwise (something could not be decided) = unclear.
 *  Never "achieved" on missing data. */
export function combineCriterionResults(results) {
    if (results.length === 0)
        return 'unclear';
    if (results.some((r) => r === 'not_met'))
        return 'not_achieved';
    return results.every((r) => r === 'met') ? 'achieved' : 'unclear';
}
export const criterionEvidenceSchema = z
    .object({
    /** Position in the window's criteria list. */
    index: z.number().int().min(0).max(MAX_CHANGE_CRITERIA - 1),
    kind: z.enum(['metric', 'alert-cleared', 'sensor-ok']),
    result: z.enum(CRITERION_RESULTS),
    /** When the criterion was due: the occurrence's end + T. */
    dueAt: isoDateTimeSchema,
    /** The value / instant the decision rests on, when there was one. */
    observed: z.number().finite().nullable(),
    observedAt: isoDateTimeSchema.nullable(),
    /** Plain-language evidence, e.g. "latest 82 at 03:29, below 100". */
    detail: z.string().max(500),
})
    .strict();
/**
 * The AI option (#153): what is sent, bounded, and how the answer is held. Model id and region are per-environment
 * config (functions/src/config), never here.
 */
export const CHANGE_AI_MODEL = {
    /** Minutes before the occurrence's start summarised as "before". */
    beforeMinutes: 60,
    /** Individual series summarised (the biggest changes first); more are counted, not listed. */
    maxSeries: 40,
    /** Alert conditions listed per side (before / after). */
    maxAlerts: 30,
    /** Free text sent (the change's stated success text), cut to this. */
    maxSuccessChars: 2000,
    maxRationaleChars: 800,
    maxOutputTokens: 1024,
    /** The model call. */
    timeoutMs: 45_000,
    /** Loading the evidence (a BigQuery job plus Firestore reads) is bounded too. */
    evidenceTimeoutMs: 30_000,
    /** Reading the tenant's opt-in before a call. */
    optInMs: 5_000,
    /** Writing the usage row and the assessment. */
    recordMs: 5_000,
    /** The change sweep's own function timeout (schedulers.ts uses this value), and the margin kept under it. */
    sweepTimeoutMs: 120_000,
    safetyMs: 10_000,
    /** Spacing between attempts at one occurrence (each is a paid call). */
    retryEveryMs: 10 * 60_000,
};
/**
 * The AI phase never STARTS a call it cannot finish and record inside the sweep's timeout: a call is started only while
 * the elapsed time since the sweep began is at most this. (A killed function would leave a paid call with no usage row.)
 */
export const CHANGE_AI_START_DEADLINE_MS = CHANGE_AI_MODEL.sweepTimeoutMs -
    (CHANGE_AI_MODEL.optInMs + CHANGE_AI_MODEL.evidenceTimeoutMs + CHANGE_AI_MODEL.timeoutMs + CHANGE_AI_MODEL.recordMs) -
    CHANGE_AI_MODEL.safetyMs;
/** How an AI call ended. Anything but `ok` is recorded as `unclear` (it never blocks or changes a verdict). */
export const AI_OUTCOMES = ['ok', 'not-enabled', 'unavailable', 'blocked', 'invalid-output', 'timeout', 'error'];
export const aiAnalysisSchema = z
    .object({
    outcome: z.enum(AI_OUTCOMES),
    model: z.string().min(1).max(200).nullable(),
    /** The model's own confidence, 0..1; null unless the call succeeded. */
    confidence: z.number().min(0).max(1).nullable(),
    /** Short rationale citing the evidence used (outcome `ok`), or why there is no answer. */
    rationale: z.string().max(CHANGE_AI_MODEL.maxRationaleChars),
    inputTokens: z.number().int().nonnegative().nullable(),
    outputTokens: z.number().int().nonnegative().nullable(),
    latencyMs: z.number().int().nonnegative().nullable(),
})
    .strict();
/** One row per AI model call: cost visibility (tokens, latency, outcome), kept for every call, failed or not. */
export const aiUsageEventSchema = z
    .object({
    feature: z.literal('change-intent'),
    model: z.string().min(1).max(200),
    outcome: z.enum(AI_OUTCOMES),
    inputTokens: z.number().int().nonnegative().nullable(),
    outputTokens: z.number().int().nonnegative().nullable(),
    latencyMs: z.number().int().nonnegative(),
    changeWindowId: idSchema,
    occurrenceStartsAt: isoDateTimeSchema,
})
    .extend(baseEntityFields)
    .strict();
/** Rung 3's machine result, at most one per `(changeWindowId, occurrenceStartsAt)` (derived id, created only if
 *  absent), written once the grading is due. `structured`: graded from the window's criteria. `ai`: Gemini's answer,
 *  or `unclear` with the reason when the call could not be made. Advisory: it never changes the verdict. */
export const changeIntentAssessmentSchema = z
    .object({
    ...occurrenceFields,
    /** Records written before the AI option are all structured. */
    method: z.enum(['structured', 'ai']).default('structured'),
    result: z.enum(INTENT_RESULTS),
    criteria: z.array(criterionEvidenceSchema).max(MAX_CHANGE_CRITERIA).default([]),
    ai: aiAnalysisSchema.nullable().default(null),
    assessedAt: isoDateTimeSchema,
})
    .extend(baseEntityFields)
    .strict()
    .superRefine((val, ctx) => {
    if (val.method === 'structured') {
        if (val.criteria.length === 0 || val.ai !== null) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['criteria'], message: 'a structured assessment has criteria and no AI analysis' });
        }
        else if (val.result !== combineCriterionResults(val.criteria.map((c) => c.result))) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['result'], message: 'result must be the combination of the criteria results' });
        }
        return;
    }
    if (val.criteria.length > 0 || val.ai === null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['ai'], message: 'an AI assessment has an AI analysis and no criteria' });
        return;
    }
    // Anything that is not a clean answer is "unclear", never a conclusion; a clean answer carries its confidence.
    if (val.ai.outcome !== 'ok' && (val.result !== 'unclear' || val.ai.confidence !== null)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['result'], message: 'a failed or unavailable AI call is unclear, with no confidence' });
    }
    if (val.ai.outcome === 'ok' && val.ai.confidence === null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['ai', 'confidence'], message: 'a successful AI call carries its confidence' });
    }
});
/** A person's mark on one occurrence. Append-only: many per occurrence, each audited; the latest decides, and a
 *  mark overrides a machine result without erasing it. */
export const changeIntentMarkInputSchema = z
    .object({
    occurrenceStartsAt: z.string().datetime({ offset: true }),
    result: z.enum(['achieved', 'not_achieved']),
    note: z.string().trim().max(2000).optional(),
})
    .strict();
export const changeIntentMarkSchema = z
    .object({
    ...occurrenceFields,
    result: z.enum(['achieved', 'not_achieved']),
    note: z.string().max(2000).nullable(),
    markedBy: idSchema,
    markedAt: isoDateTimeSchema,
})
    .extend(baseEntityFields)
    .strict();
export const INTENT_VIEW_RESULTS = ['achieved', 'not_achieved', 'unclear', 'not_graded'];
export const MAX_MARKS_SHOWN = 10;
/** What the API returns for a verdict: the stored verdict plus rung 2's state. `rung` is 2 exactly when the
 *  comparison was assessed; nothing here changes `verdict`. */
export const BASELINE_STATES = ['pending', 'done', 'unavailable', 'not-applicable'];
/** A witness was impacted during the window (immediately reportable). Many per
 *  occurrence, each with its own id; `(changeWindowId, occurrenceStartsAt)` is
 *  the partition key, not the identity. */
export const changeObservationSchema = z
    .object({
    ...occurrenceFields,
    kind: z.literal('witness-impact'),
    dedupKey: z.string().min(1).max(700),
    alertId: idSchema,
    severity: z.enum(ALERT_SEVERITIES),
    /** Set when the condition already existed at the start and has ESCALATED. */
    snapshotSeverity: z.enum(ALERT_SEVERITIES).nullable(),
    observedAt: isoDateTimeSchema,
})
    .extend(baseEntityFields)
    .strict();
//# sourceMappingURL=change.schema.js.map